import dayjs from 'dayjs';

/** 今日 00:00:00 的 dayjs 对象，用于时间范围选择器的默认起始值 */
export const START_TIME = dayjs('00:00:00', 'HH:mm:ss');
/** 今日 23:59:59 的 dayjs 对象，用于时间范围选择器的默认结束值 */
export const END_TIME = dayjs('23:59:59', 'HH:mm:ss');

const normalizeTimestamp = (date: string | number | Date | undefined) => {
  const numericDate =
    typeof date === 'number'
      ? date
      : typeof date === 'string' && /^-?\d{9,13}$/.test(date.trim())
      ? Number(date)
      : undefined;

  if (numericDate === undefined || !Number.isFinite(numericDate)) return date;

  // Java 服务可能返回秒级或毫秒级时间戳，秒级值需先统一转换为毫秒。
  return Math.abs(numericDate) < 100_000_000_000
    ? numericDate * 1000
    : numericDate;
};

/**
 * 格式化日期，默认格式 YYYY-MM-DD HH:mm:ss。
 * 底层使用 dayjs，支持字符串、秒/毫秒时间戳、Date 对象作为输入。
 */
export const formatDate = (
  date?: string | number | Date,
  format = 'YYYY-MM-DD HH:mm:ss'
) => dayjs(normalizeTimestamp(date)).format(format);

/**
 * 格式化销量显示：超过 9999 显示 "9999+"，空值返回 0。
 * 用于列表页销量字段的展示截断。
 */
export const formatSale = (value: string | number) => {
  if (!value) {
    return 0;
  }
  if (Number(value) > 9999) {
    return '9999+';
  }
  return value;
};

const ONE_MINUTE = 1000 * 60;
const ONE_HOUR = ONE_MINUTE * 60;
const ONE_DAY = ONE_HOUR * 24;

/**
 * 将未来时间戳格式化为剩余时长字符串（如 "2天3小时"、"5分钟30秒"）。
 * 时间戳已过期时返回 "已到期"，空值返回空字符串。
 */
export const formatExpiredTime = (timestamp: number): string => {
  if (!timestamp) return '';

  const current = Date.now();
  const diff = timestamp - current;

  if (diff > ONE_DAY) {
    const day = Math.floor(diff / ONE_DAY);
    const hour = Math.floor((diff % ONE_DAY) / ONE_HOUR);
    const minute = Math.floor((diff % ONE_HOUR) / ONE_MINUTE);
    return `${day}天${hour > 0 ? `${hour}小时` : ''}${
      minute > 0 ? `${minute}分` : ''
    }`;
  } else if (diff > ONE_HOUR) {
    const hour = Math.floor(diff / ONE_HOUR);
    const minute = Math.floor((diff % ONE_HOUR) / ONE_MINUTE);
    return `${hour}小时${minute > 0 ? `${minute}分` : ''}`;
  } else if (diff > ONE_MINUTE) {
    const minute = Math.floor(diff / ONE_MINUTE);
    const second = Math.floor((diff % ONE_MINUTE) / 1000);
    return `${minute}分钟${second > 0 ? `${second}秒` : ''}`;
  } else if (diff >= 1000) {
    return `${Math.floor(diff / 1000)}秒`;
  } else {
    return '已到期';
  }
};

// 取 hostname 的二级域名部分（倒数第二段），如 static.lingshu365.com → lingshu365
const getSecondLevelDomain = (hostname: string): string => {
  const parts = hostname.split('.');
  return parts.length >= 2 ? parts[parts.length - 2] : hostname;
};

const PROCESSABLE_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'bmp',
  'gif',
  'webp',
  'tif',
  'tiff',
  'heic',
];

const isOssUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    const { hostname } = new URL(url);
    const sld = getSecondLevelDomain(hostname);
    return ['lingshu365', 'erkangai'].includes(sld);
  } catch {
    return false;
  }
};

const getUrlPathname = (url: string): string => {
  try {
    const baseUrl =
      typeof window !== 'undefined' ? window.location.href : 'http://localhost';
    return new URL(url, baseUrl).pathname;
  } catch {
    return url.split(/[?#]/)[0];
  }
};

const isProcessableImageUrl = (url: string): boolean => {
  const extension = getUrlPathname(url)
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/)?.[1];
  return !!extension && PROCESSABLE_IMAGE_EXTENSIONS.includes(extension);
};

const appendOssProcess = (url: string, process: string): string => {
  if (url.includes('x-oss-process=')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}x-oss-process=${process}`;
};

/**
 * 图片缩略图（等比缩放，不裁剪）。
 * 仅对指定图片类型追加 x-oss-process 参数，其他 URL 原样返回。
 * width/height 以 iPhone 6（375pt）为基准，内部自动 ×2.6 换算为实际像素。
 */
export const formatImg = (
  url: string,
  width: number,
  height?: number
): string => {
  if (!url) return '';
  const w = Math.floor(width * 1.3 * 2);
  if (isOssUrl(url) && isProcessableImageUrl(url)) {
    return appendOssProcess(url, `image/resize,w_${w}`);
  }
  return url;
};

/**
 * 图片裁剪缩略图（指定宽高裁剪）。
 * 仅对指定图片类型追加 x-oss-process 参数，其他 URL 原样返回。
 * type 默认 'fill'（填充裁剪），width/height 以 iPhone 6（375pt）为基准。
 */
export const formatImgCut = (
  url: string,
  width: number,
  height?: number,
  type = 'fill'
): string => {
  if (!url) return '';
  const h = height ?? width;
  const w = Math.floor(width * 1.3 * 2);
  const ph = Math.floor(h * 1.3 * 2);
  if (isOssUrl(url) && isProcessableImageUrl(url)) {
    return appendOssProcess(
      url,
      `image/resize,m_${type},h_${ph},w_${w},limit_0`
    );
  }
  return url;
};

/**
 * 将时间戳格式化为相对日期字符串：
 * - 今天：`今天 HH:mm`
 * - 昨天：`昨天 HH:mm`
 * - 同年：`M月D日 HH:mm`
 * - 跨年：`YYYY年M月D日 HH:mm`
 */
export const formatPublishTime = (timestamp: number): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const time = `${hh}:${mm}`;
  if (dateOnly.getTime() === today.getTime()) return `今天 ${time}`;
  if (dateOnly.getTime() === yesterday.getTime()) return `昨天 ${time}`;
  if (date.getFullYear() === now.getFullYear())
    return `${date.getMonth() + 1}月${date.getDate()}日 ${time}`;
  return `${date.getFullYear()}年${
    date.getMonth() + 1
  }月${date.getDate()}日 ${time}`;
};
